import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { rampsVersion, subscribeRamps } from "./lib/colorscale";
import { ApiError } from "./api";
import { MAP_HOME, isHome, panBy, zoomAt, type MapView } from "./lib/basemap";
import { trackAt } from "./lib/tracklayout";

interface AsyncState<T> {
  data: T | null;
  error: string | null;
  /**
   * Muatan galat apa adanya, bagi permintaan yang ditolak sambil membawa
   * keterangan.
   *
   * Sebagian penolakan bukan "gagal" melainkan "tidak, dan inilah sebabnya
   * dalam bentuk yang bisa digambar": jaring radius yang menangkap terlalu
   * banyak sumur memulangkan 400 berikut tabel jaringnya, sebab yang
   * dibutuhkan untuk menggeser radiusnya adalah justru tabel itu. Kalimatnya
   * tetap di `error`; yang di sini adalah badannya.
   */
  errorBody: unknown;
  loading: boolean;
  reload: () => void;
}

/**
 * Load data with cancellation and a manual reload handle.
 *
 * The abort matters here: switching wells while a 3000-sample log request is in
 * flight would otherwise let the stale response overwrite the new one.
 */
export function useAsync<T>(
  loader: (signal: AbortSignal) => Promise<T>,
  deps: unknown[],
  options: { skip?: boolean } = {},
): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorBody, setErrorBody] = useState<unknown>(null);
  const [loading, setLoading] = useState(!options.skip);
  const [nonce, setNonce] = useState(0);
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  useEffect(() => {
    if (options.skip) {
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    let live = true;
    setLoading(true);
    setError(null);
    setErrorBody(null);
    loaderRef
      .current(controller.signal)
      .then((result) => {
        if (live) setData(result);
      })
      .catch((cause: unknown) => {
        if (!live || controller.signal.aborted) return;
        if (cause instanceof DOMException && cause.name === "AbortError") return;
        setError(cause instanceof ApiError ? cause.detail : String(cause));
        setErrorBody(cause instanceof ApiError ? cause.body : null);
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce, options.skip]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { data, error, errorBody, loading, reload };
}

/** Debounce a rapidly changing value — parameter sliders that trigger a recompute. */
export function useDebounced<T>(value: T, delay = 350): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

/** A value persisted in localStorage — panel widths, last well, chosen tracks. */
function readStored<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

export function useStored<T>(key: string, fallback: T): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(() => readStored(key, fallback));

  // Kunci yang berubah harus dibaca ulang. Beberapa preferensi disimpan per
  // proyek — `evolog.corr.curvecolor.${id}`, acuan kedalaman — dan React
  // menyimpan komponen yang rutenya sepola tetap terpasang ketika hanya
  // parameternya berganti. Tanpa ini, berpindah proyek membawa serta nilai
  // milik proyek sebelumnya dan menuliskannya ke kunci proyek yang baru pada
  // sentuhan pertama: bukan preferensi yang hilang, melainkan preferensi
  // sebuah lapangan yang menimpa lapangan lain.
  const seen = useRef(key);
  if (seen.current !== key) {
    seen.current = key;
    setValue(readStored(key, fallback));
  }

  const update = useCallback(
    (next: T) => {
      setValue(next);
      try {
        localStorage.setItem(key, JSON.stringify(next));
      } catch {
        /* private browsing — the preference simply does not persist */
      }
    },
    [key],
  );
  return [value, update];
}

/**
 * Menyelaraskan sumur yang dikerjakan sebuah layar dengan pemilih di cangkang.
 *
 * Pemilih WELL di kiri atas berlaku di seluruh aplikasi: ia yang dibaca tombol
 * CPI A3, baris status, dan tiap layar yang membuka dirinya pada sebuah sumur.
 * Sampai sekarang alirannya satu arah — turun. Sebuah layar yang memilih
 * sumurnya sendiri, seperti lembar LRLC atau sumur yang ditaksir di Estimate,
 * tidak pernah mengabarkannya ke atas, jadi pemilih itu boleh menunjuk KTI-01
 * sementara seluruh layar membaca KTI-07. Yang mencetak CPI kemudian mencetak
 * sumur yang tidak sedang dibacanya, dan tidak ada apa pun di layarnya yang
 * mengabarkan bahwa keduanya berbeda.
 *
 * `focused` adalah sumur yang **sedang dikerjakan layar itu**, atau null ketika
 * layarnya memang tidak sedang berdiri di atas satu sumur — scope crossplot
 * berisi enam sumur, penampang korelasi, daftar batch. Null berarti "jangan
 * diselaraskan": sebuah pemilih tunggal tidak bisa mewakili enam sumur, dan
 * menunjuk yang pertama di antaranya adalah nama yang bukan nama yang sedang
 * dibaca orang.
 *
 * Arahnya ditentukan oleh **siapa yang baru saja bergerak**, dan itulah sebab
 * kait ini ada alih-alih sepasang efek di tiap layar. Dua efek yang saling
 * memanggil tanpa mengingat siapa yang mulai adalah gelang: layar melapor,
 * cangkang berpindah, layar menyesuaikan diri, melapor lagi. Di sini
 * `lastActive` mengingatnya, dan tiap perpindahan hanya menyeberang sekali.
 *
 * Pada pemasangan, cangkang yang menang. Seseorang yang memilih KTI-07 di
 * pemilih lalu membuka Crossplot bermaksud melihat KTI-07 — dan scope yang
 * tersimpan di peramban sejak minggu lalu, betapa pun ia juga "sedang
 * dikerjakan", adalah pilihan yang lebih tua daripada klik yang baru saja
 * terjadi.
 *
 * `adoptOnMount: false` membalik yang terakhir itu, dan ia ada untuk layar yang
 * sumurnya tertulis di **alamat**. `?sheet=12` bukan keadaan yang dipulihkan
 * melainkan kalimat yang baru saja diucapkan seseorang — sebuah tautan yang
 * dikirim ke sebelahnya, atau butir menubar yang barusan diklik — dan cangkang
 * yang menariknya ke sumur aktif penerimanya membuat tautan itu membuka lembar
 * sumur yang lain daripada yang dimaksud pengirimnya, dengan alamat di bilah
 * atas yang tetap menyebut sumur yang benar.
 */
export function useWellSync(
  focused: number | null,
  active: number | null,
  handlers: {
    /** Layar berpindah sendiri: pemilih di cangkang mengikutinya. */
    onFocus?: (wellId: number) => void;
    /** Cangkang berpindah: layar ini yang mengikutinya. */
    onAdopt?: (wellId: number) => void;
    /**
     * Siapa yang menang pada pemasangan. Bawaannya cangkang; `false` bagi
     * layar yang sumurnya datang dari alamat — lihat keterangan di atas.
     */
    adoptOnMount?: boolean;
  },
) {
  // Dibaca lewat ref supaya dependensi efeknya cukup sepasang id. Handler yang
  // ikut ke dalam dependensi adalah fungsi baru pada tiap render — yaitu efek
  // yang berjalan pada tiap render, dan dengan `lastActive` yang ikut disegarkan
  // di sana, arahnya ditentukan oleh render dan bukan oleh perpindahan.
  const latest = useRef(handlers);
  latest.current = handlers;

  const lastActive = useRef<number | null | undefined>(undefined);
  useEffect(() => {
    const moved =
      lastActive.current === undefined
        ? latest.current.adoptOnMount !== false
        : lastActive.current !== active;
    lastActive.current = active;
    if (focused === null) return;
    // Cangkang tanpa sumur aktif sama sekali — proyek yang baru saja selesai
    // memuat. Yang punya jawaban di sini adalah layarnya.
    if (active === null) {
      latest.current.onFocus?.(focused);
      return;
    }
    if (focused === active) return;
    if (moved) latest.current.onAdopt?.(active);
    else latest.current.onFocus?.(focused);
  }, [focused, active]);
}

/**
 * Ukuran sebuah elemen, diikuti lewat ResizeObserver.
 *
 * Yang dikembalikan adalah ref callback, bukan objek ref, dan itu disengaja.
 * Pemasangan lewat efek berdependensi kosong hanya sempat melihat elemen yang
 * sudah ada saat mount: sebuah peta yang menunggu datanya merender teks
 * "belum ada sumur" lebih dulu, sehingga kotaknya baru lahir beberapa render
 * kemudian — dan observer yang tidak pernah terpasang membuat ukurannya
 * tertinggal di nol selamanya, yaitu peta kosong yang tak pernah menggambar
 * apa pun. Ref callback dipanggil tepat saat elemennya muncul dan hilang, kapan
 * pun itu terjadi.
 */
export function useElementSize<T extends HTMLElement>() {
  const [size, setSize] = useState({ width: 0, height: 0 });
  const observer = useRef<ResizeObserver | null>(null);
  const measure = useCallback((width: number, height: number) => {
    // Ukuran yang sama tidak melahirkan objek baru: peta yang menghitung ulang
    // kisi konturnya setiap kali observer berbunyi akan bekerja tanpa hasil.
    setSize((prev) =>
      prev.width === width && prev.height === height ? prev : { width, height },
    );
  }, []);
  const ref = useCallback(
    (element: T | null) => {
      observer.current?.disconnect();
      observer.current = null;
      if (!element) return;
      // Ukur sekali di muka: di lingkungan tanpa ResizeObserver — jsdom, dan
      // peramban lama — inilah satu-satunya ukuran yang akan didapat.
      const rect = element.getBoundingClientRect();
      measure(rect.width, rect.height);
      if (typeof ResizeObserver === "undefined") return;
      const next = new ResizeObserver(([entry]) => {
        const { width, height } = entry.contentRect;
        measure(width, height);
      });
      next.observe(element);
      observer.current = next;
    },
    [measure],
  );
  useEffect(() => () => observer.current?.disconnect(), []);
  return [ref, size] as const;
}

/**
 * Zoom dan geseran sebuah peta, lengkap dengan roda tetikus dan seretan.
 *
 * Tiga peta di aplikasi ini — peta lapangan, basemap korelasi, dan peta model
 * geologi — sebelumnya memuat seluruh lapangan ke dalam kotaknya lalu berhenti
 * di situ. Pada lapangan bersumur enam belas yang berdempetan, itu berarti
 * simbol yang bertumpuk dan nama yang tak terbaca, tanpa jalan keluar.
 *
 * Perilakunya sengaja sama di ketiganya: roda memperbesar di sekitar kursor,
 * seret menggeser, dan sebuah seretan tidak boleh berubah menjadi klik — di
 * basemap, klik berarti mengeluarkan sumur dari korelasi, dan kehilangan satu
 * kolom panel karena tangan bergeser dua piksel adalah kerusakan, bukan
 * kejutan kecil.
 *
 * `space` memetakan piksel elemen ke ruang tempat pandangan itu hidup. Peta
 * SVG yang menggambar dalam koordinat layar tidak memerlukannya; peta yang
 * menggambar di kisi abstrak dan dipasangi `preserveAspectRatio` memerlukannya,
 * sebab piksel ke-40 elemennya bukan unit ke-40 viewBox-nya.
 */
export function useMapView(options: {
  space?: (rect: DOMRect) => { scale: number; offsetX: number; offsetY: number };
  /** Seberapa jauh jari boleh bergeser sebelum niatnya dianggap menyeret. */
  dragThreshold?: number;
  /**
   * Seretan mana yang berarti menggeser peta. Bawaannya tombol kiri, tetapi
   * pada tampilan yang seretannya sudah punya arti lain — irisan tegak model
   * geologi, tempat menyeret berarti memindahkan garis horizon — pemanggil
   * memundurkannya ke Shift atau tombol tengah.
   */
  panWhen?: (event: PointerEvent) => boolean;
} = {}) {
  const { space, dragThreshold = 4, panWhen } = options;
  const [view, setView] = useState<MapView>(MAP_HOME);
  const [panning, setPanning] = useState(false);
  const element = useRef<Element | null>(null);
  const detach = useRef<(() => void) | null>(null);
  const spaceRef = useRef(space);
  spaceRef.current = space;
  const panWhenRef = useRef(panWhen);
  panWhenRef.current = panWhen;

  const localSpace = useCallback((rect: DOMRect) => {
    return spaceRef.current?.(rect) ?? { scale: 1, offsetX: 0, offsetY: 0 };
  }, []);

  const bind = useCallback(
    (el: Element | null) => {
      detach.current?.();
      detach.current = null;
      element.current = el;
      if (!el) return;

      const drag = { active: false, moved: false, x: 0, y: 0 };

      const onWheel = (event: WheelEvent) => {
        // Halaman di belakang peta tidak boleh ikut bergulir saat peta
        // diperbesar, jadi pendengar ini harus non-pasif.
        event.preventDefault();
        const rect = el.getBoundingClientRect();
        const { scale, offsetX, offsetY } = localSpace(rect);
        const ax = (event.clientX - rect.left - offsetX) / scale;
        const ay = (event.clientY - rect.top - offsetY) / scale;
        const factor = event.deltaY > 0 ? 1 / 1.15 : 1.15;
        setView((current) => zoomAt(current, factor, ax, ay));
      };

      const onPointerDown = (event: PointerEvent) => {
        const allowed = panWhenRef.current ?? ((e: PointerEvent) => e.button === 0);
        if (!allowed(event)) return;
        drag.active = true;
        drag.moved = false;
        drag.x = event.clientX;
        drag.y = event.clientY;
      };

      const onPointerMove = (event: PointerEvent) => {
        if (!drag.active) return;
        const dx = event.clientX - drag.x;
        const dy = event.clientY - drag.y;
        if (!drag.moved && Math.hypot(dx, dy) < dragThreshold) return;
        if (!drag.moved) {
          drag.moved = true;
          setPanning(true);
          el.setPointerCapture?.(event.pointerId);
        }
        drag.x = event.clientX;
        drag.y = event.clientY;
        const { scale } = localSpace(el.getBoundingClientRect());
        setView((current) => panBy(current, dx / scale, dy / scale));
      };

      const endDrag = (event: PointerEvent) => {
        if (!drag.active) return;
        drag.active = false;
        if (drag.moved) {
          setPanning(false);
          el.releasePointerCapture?.(event.pointerId);
        }
      };

      const onClickCapture = (event: MouseEvent) => {
        // Seretan yang berakhir di atas sebuah sumur tidak boleh terbaca
        // sebagai klik pada sumur itu.
        if (!drag.moved) return;
        drag.moved = false;
        event.stopPropagation();
        event.preventDefault();
      };

      // Elemen di sini boleh HTML maupun SVG, dan peta kejadian `Element`
      // tidak mengenal roda maupun penunjuk — jadi pendengarnya dipasang
      // sebagai EventListener biasa.
      const listeners: [string, EventListener, (boolean | AddEventListenerOptions)?][] = [
        ["wheel", onWheel as EventListener, { passive: false }],
        ["pointerdown", onPointerDown as EventListener, undefined],
        ["pointermove", onPointerMove as EventListener, undefined],
        ["pointerup", endDrag as EventListener, undefined],
        ["pointercancel", endDrag as EventListener, undefined],
        ["click", onClickCapture as EventListener, true],
      ];
      for (const [name, handler, opts] of listeners) el.addEventListener(name, handler, opts);

      detach.current = () => {
        for (const [name, handler, opts] of listeners) {
          el.removeEventListener(name, handler, typeof opts === "boolean" ? opts : undefined);
        }
      };
    },
    [dragThreshold, localSpace],
  );

  useEffect(() => () => detach.current?.(), []);

  /** Memperbesar dari tengah kotak — yang dipakai tombol + dan −. */
  const zoomBy = useCallback(
    (factor: number) => {
      const rect = element.current?.getBoundingClientRect();
      if (!rect) {
        setView((current) => zoomAt(current, factor, 0, 0));
        return;
      }
      const { scale, offsetX, offsetY } = localSpace(rect);
      const ax = (rect.width / 2 - offsetX) / scale;
      const ay = (rect.height / 2 - offsetY) / scale;
      setView((current) => zoomAt(current, factor, ax, ay));
    },
    [localSpace],
  );

  const reset = useCallback(() => setView(MAP_HOME), []);

  return { view, setView, zoomBy, reset, bind, panning, home: isHome(view) };
}

/**
 * Menyusun ulang sederet kotak mendatar dengan menyeretnya.
 *
 * Dipakai kepala track lembar log dan kepala kolom lembar korelasi. Keduanya
 * adalah deretan yang sama bentuknya — sederet anak yang bersebelahan, salah
 * satunya dipegang lalu dibawa ke tempat lain — dan seretan yang ditulis dua
 * kali adalah dua tempat yang akan berselisih tentang berapa piksel sebuah
 * tekanan boleh bergeser sebelum ia bukan tekanan lagi.
 *
 * Yang harus diputuskan, dan sudah:
 *
 * - Tekanan tetap tekanan sampai lima piksel. Kepala track di lembar log juga
 *   memilih tracknya ketika diklik, dan sebuah lembar yang menyusun ulang
 *   dirinya karena tangan yang bergetar dua piksel adalah lembar yang tidak
 *   bisa dipakai.
 * - Kendali di dalam kepala tidak ikut terseret. Baris legenda kurva membuka
 *   kotak suntingnya, lencana LIN/LOG menukar sumbunya; menekan keduanya
 *   berarti menekan, bukan memindahkan.
 * - Kotak-kotaknya diukur sekali di awal seretan. Kotak yang diukur ulang
 *   sepanjang seretan adalah kotak yang bergerak karena seretan itu sendiri.
 * - Klik yang lahir dari seretan ditelan. Tanpa itu, menyeret sebuah kepala
 *   sekaligus memilih tracknya — dan rel parameter berpindah tab di akhir
 *   setiap seretan.
 */
const REORDER_THRESHOLD_PX = 5;

export function useReorderDrag(onReorder: (from: number, to: number) => void) {
  const [drag, setDrag] = useState<{ from: number; to: number } | null>(null);
  const gesture = useRef<
    { from: number; boxes: DOMRect[]; startX: number; moved: boolean } | null
  >(null);
  const dragged = useRef(false);

  /**
   * Menekan sebuah kotak — atau pegangan di dalamnya.
   *
   * Kotaknya dicari lewat `closest`, bukan lewat `currentTarget`: pada rel
   * track keduanya sama, sedangkan pada panel sumur lembar korelasi yang
   * ditekan adalah kepala panelnya dan yang diukur adalah panelnya.
   */
  const beginDrag = (event: React.PointerEvent<HTMLElement>) => {
    if (event.button !== 0) return;
    // Tekanan baru selalu dimulai bersih. Klik yang lahir dari seretan ditelan
    // di bawah, tetapi klik itu tidak selalu datang — peramban tidak
    // mengirimkannya ketika tapaknya berakhir di luar kotak asalnya — dan
    // tanda yang tidak pernah dipakai akan menelan klik berikutnya, yaitu klik
    // sungguhan sesudah seretan.
    dragged.current = false;
    const target = event.target as HTMLElement;
    if (target.closest("button, input, select, textarea")) return;
    const item = event.currentTarget.closest<HTMLElement>("[data-reorder-index]");
    const row = item?.parentElement;
    if (!item || !row) return;
    const siblings = [...row.querySelectorAll<HTMLElement>(":scope > [data-reorder-index]")];
    gesture.current = {
      from: Number(item.getAttribute("data-reorder-index")),
      boxes: siblings.map((node) => node.getBoundingClientRect()),
      startX: event.clientX,
      moved: false,
    };
  };

  const moveDrag = (event: React.PointerEvent<HTMLElement>) => {
    const active = gesture.current;
    if (!active) return;
    if (!active.moved) {
      if (Math.abs(event.clientX - active.startX) < REORDER_THRESHOLD_PX) return;
      active.moved = true;
      event.currentTarget.setPointerCapture(event.pointerId);
    }
    const to = trackAt(active.boxes, event.clientX);
    if (to >= 0) setDrag({ from: active.from, to });
  };

  const finish = (event: React.PointerEvent<HTMLElement>) => {
    const active = gesture.current;
    gesture.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    setDrag(null);
    if (!active?.moved) return;
    dragged.current = true;
    if (drag && drag.to !== drag.from) onReorder(drag.from, drag.to);
  };

  return {
    /** Ditaruh pada deretan itu sendiri: klik sisa seretan berhenti di sini. */
    rowProps: {
      onClickCapture: (event: React.MouseEvent) => {
        if (!dragged.current) return;
        dragged.current = false;
        event.stopPropagation();
      },
    },
    /**
     * Pegangan seret saja, tanpa penanda kotaknya.
     *
     * Dipakai bersama `boxProps` ketika yang ditekan bukan kotak yang diukur:
     * kepala panel sumur lembar korelasi menyeret seluruh panelnya, dan panel
     * itulah yang punya `data-reorder-index`. Yang menekan tetap menemukan
     * kotaknya lewat `closest`, jadi keduanya tidak perlu benda yang sama.
     *
     * Tanpa gaya: pegangan seperti ini duduk di elemen yang gayanya sudah
     * milik pemasangnya — sebuah `style` di sini akan tertimpa oleh `style`
     * elemen itu tanpa suara. `touch-action: none` karena itu ditulis di sana.
     */
    handleProps: {
      onPointerDown: beginDrag,
      onPointerMove: moveDrag,
      onPointerUp: finish,
      onPointerCancel: finish,
    },
    /**
     * Penanda kotaknya saja, tanpa penangan apa pun — pasangan `handleProps`.
     *
     * Kotak yang pegangannya terpisah tidak boleh ikut mendengarkan tekanan:
     * sebuah panel korelasi memuat lembar log yang seretannya memilih interval
     * dan rel kepala kurva yang seretannya menyusun ulang kurva, dan keduanya
     * mengapung naik sampai ke panel itu.
     */
    boxProps: (index: number) => ({ "data-reorder-index": index }),
    /** Ditaruh pada tiap kotak, berikut nomor urutnya. */
    itemProps: (index: number) => ({
      "data-reorder-index": index,
      // Tanpa ini, jari yang menyeret mendatar menggulirkan lembarnya.
      style: { touchAction: "none" as const },
      onPointerDown: beginDrag,
      onPointerMove: moveDrag,
      onPointerUp: finish,
      onPointerCancel: finish,
    }),
    /** Kotak yang sedang dibawa, dan tempat ia akan mendarat. */
    dragging: drag?.from ?? null,
    dropIndex: drag?.to ?? null,
  };
}

/**
 * Nomor versi pendaftaran palet — berubah setiap kali sebuah palet disunting.
 *
 * Pelukis warna menyalin stop rampnya ketika dibuat, dan yang menyimpannya di
 * `useMemo` atau menggambar di `useEffect` tidak tergambar ulang ketika warna
 * palet yang sama diubah: kuncinya tetap `user_…` yang sama. Nomor ini
 * dimasukkan ke dependensinya supaya warna yang baru saja disimpan benar-benar
 * tampil di layar yang sedang terbuka.
 */
export function useRampsVersion(): number {
  return useSyncExternalStore(subscribeRamps, rampsVersion, rampsVersion);
}
